import { createElement } from 'react'
import chroma from 'chroma-js'
import { sortBy } from 'lodash-es'
import {
  createChartTheme,
  TIMP_CHART_COLORS,
  type PieChartLegendClickData,
  type PieChartProps
} from '@jana-technology/platform-ui/charts'

import {
  chopAndFormatNumberWithCommas,
  formatNumberWithCommas
} from 'common/utils/chart/chartUtils'

import {
  type PieTooltipVariant,
  type RiskByCategoryPieChartAcceptanceCategory,
  type RiskByCategoryPieChartCategoryResult,
  type RiskByCategoryRangeValueType
} from './types'

export const hasPieChartData = (pies?: PieChartProps['pies']) =>
  pies?.some((pie) => pie.data.some((datum) => datum.value !== 0)) ?? false

export type ActivePieDataById = Record<
  string,
  PieChartProps['pies'][number]['activeData']
>

export const getUpdatedActivePieDataById = (
  activeDataByPieId: ActivePieDataById,
  { activeSliceCount, visiblePie }: PieChartLegendClickData
): ActivePieDataById => {
  const nextActiveDataByPieId = { ...activeDataByPieId }

  if (activeSliceCount === visiblePie.data.length) {
    delete nextActiveDataByPieId[visiblePie.id]
  } else {
    nextActiveDataByPieId[visiblePie.id] = visiblePie.activeData ?? []
  }

  return nextActiveDataByPieId
}

export const getRiskByCategoryItems = (
  data?: {
    categoriesResult?: RiskByCategoryPieChartCategoryResult[]
    items?: RiskByCategoryPieChartCategoryResult[]
  } | null
) => data?.items ?? data?.categoriesResult ?? []

export const getOrderedAcceptanceCategories = (
  acceptanceCategories: RiskByCategoryPieChartAcceptanceCategory[] = []
) =>
  [...acceptanceCategories].sort(
    (left, right) =>
      left.minDensityValue - right.minDensityValue ||
      left.maxDensityValue - right.maxDensityValue
  )

export const getOrderedRiskByCategoryItems = (
  chartItems: RiskByCategoryPieChartCategoryResult[] = [],
  categoryOrder?: readonly string[]
) => {
  if (categoryOrder == null) {
    return chartItems
  }

  const orderedChartItems = categoryOrder
    .map((category) => chartItems.find((item) => item.name === category))
    .filter(
      (item): item is RiskByCategoryPieChartCategoryResult => item != null
    )

  const orderedCategoryNames = new Set(
    orderedChartItems.map((item) => item.name)
  )
  const unorderedChartItems = chartItems.filter(
    (item) => !orderedCategoryNames.has(item.name)
  )

  return [...orderedChartItems, ...unorderedChartItems]
}

export const getRiskByCategorySeries = (
  chartItems: RiskByCategoryPieChartCategoryResult[]
) => {
  const seriesNames: string[] = []

  for (const item of chartItems) {
    for (const value of item.values ?? []) {
      if (!seriesNames.includes(value.key)) {
        seriesNames.push(value.key)
      }
    }
  }

  return seriesNames
    .map((seriesName) => ({
      name: seriesName,
      values: chartItems.map(
        (item) =>
          item.values?.find((value) => value.key === seriesName)?.value ?? 0
      )
    }))
    .filter((seriesItem) => seriesItem.values.some((value) => value !== 0))
}

export const getSeriesIdSuffix = (index: number) => {
  switch (index) {
    case 0:
      return 'primary'
    case 1:
      return 'secondary'

    default:
      return `series-${index + 1}`
  }
}

const normalizeHexColor = (color: string) =>
  color.startsWith('#') ? color : `#${color}`

const formatRiskDensityThresholdValue = (value: number) => {
  if (value >= Number.MAX_VALUE) {
    return 'Max'
  }

  return formatNumberWithCommas(value, { maximumFractionDigits: 0 })
}

export const formatRiskRange = (
  {
    minDensityValue,
    maxDensityValue,
    minValue,
    maxValue
  }: Pick<
    RiskByCategoryPieChartAcceptanceCategory,
    'maxDensityValue' | 'maxValue' | 'minDensityValue' | 'minValue'
  >,
  rangeValueType: RiskByCategoryRangeValueType = 'density'
) => {
  const minThreshold = rangeValueType === 'total' ? minValue : minDensityValue
  const maxThreshold = rangeValueType === 'total' ? maxValue : maxDensityValue

  return `${formatRiskDensityThresholdValue(
    minThreshold
  )} – ${formatRiskDensityThresholdValue(maxThreshold)} RU`
}

export const getRiskByCategoryPieConfig = ({
  acceptanceCategories,
  appendRiskLabel = false,
  categoryOrder,
  chartItems,
  rangeValueType
}: {
  acceptanceCategories?: RiskByCategoryPieChartAcceptanceCategory[]
  appendRiskLabel?: boolean
  categoryOrder?: readonly string[]
  chartItems?: RiskByCategoryPieChartCategoryResult[]
  rangeValueType?: RiskByCategoryRangeValueType
}) => {
  const orderedAcceptanceCategories =
    getOrderedAcceptanceCategories(acceptanceCategories)
  const riskCategoryOrder =
    orderedAcceptanceCategories.length > 0
      ? orderedAcceptanceCategories.map((category) => category.name)
      : categoryOrder
  const orderedChartItems = getOrderedRiskByCategoryItems(
    chartItems,
    riskCategoryOrder
  )
  const acceptanceCategoryByName = new Map(
    orderedAcceptanceCategories.map((category) => [category.name, category])
  )
  const acceptanceCategoriesByChartItem = orderedChartItems.map((item) =>
    acceptanceCategoryByName.get(item.name)
  )

  const series = sortBy(getRiskByCategorySeries(orderedChartItems), 'name')

  const hasMultipleSeries = series.length > 1

  return {
    chartItems: orderedChartItems,
    series,
    hasMultipleSeries,
    tooltipVariant: hasMultipleSeries ? ('wis' as const) : ('default' as const),
    items: orderedChartItems.map((item, index) => {
      const acceptanceCategory = acceptanceCategoriesByChartItem[index]
      const shouldAppendRiskLabel =
        appendRiskLabel || acceptanceCategory != null

      return {
        id: item.id,
        name: shouldAppendRiskLabel ? `${item.name} Risk` : item.name,
        subtitle:
          acceptanceCategory == null
            ? undefined
            : createElement(
                'span',
                { className: 'text-xs text-gray-900' },
                formatRiskRange(acceptanceCategory, rangeValueType)
              )
      }
    }),
    acceptanceCategoriesByChartItem
  }
}

export const getRiskByCategoryTheme = (
  colors: Array<string | undefined>
): PieChartProps['theme'] | undefined => {
  const hasMissingColor = colors.some((color) => color == null)

  if (colors.length === 0 || hasMissingColor) {
    return undefined
  }

  const normalizedColors = colors.map((color) => normalizeHexColor(color ?? ''))

  return {
    ...createChartTheme({ colors: normalizedColors }),
    colors: normalizedColors
  }
}

export const buildPieChartConfig = ({
  id,
  name,
  seriesKey,
  items,
  values,
  activeData,
  isRiskSignificance,
  onLegendItemClick,
  tooltipVariant = 'default',
  unitSymbol
}: {
  id: string
  name?: string
  seriesKey?: string
  items: Array<{
    id?: number
    name: string
    subtitle?: React.ReactNode
  }>
  values: number[]
  activeData?: NonNullable<PieChartProps['pies'][number]['activeData']>
  isRiskSignificance: boolean
  onLegendItemClick?: NonNullable<
    PieChartProps['pies'][number]['onLegendItemClick']
  >
  tooltipVariant?: PieTooltipVariant
  unitSymbol?: string
}): PieChartProps['pies'][number] => {
  const pieTotal = values.reduce((total, value) => total + value, 0)
  const activeLabels =
    activeData == null
      ? undefined
      : new Set(activeData.map((datum) => datum.label))
  const activePieTotal =
    activeLabels == null
      ? pieTotal
      : items.reduce(
          (total, item, index) =>
            total + (activeLabels.has(item.name) ? (values[index] ?? 0) : 0),
          0
        )
  const data = items
    .map((item, index) => {
      const value = values[index] ?? 0
      const tooltipPieTotal = activeLabels?.has(item.name)
        ? activePieTotal
        : pieTotal

      return {
        categoryId: item.id,
        label: item.name,
        seriesKey,
        subtitle: item.subtitle,
        title: tooltipVariant === 'wis' ? name : undefined,
        value,
        customValue: formatPieTooltipValue(
          value,
          tooltipPieTotal,
          isRiskSignificance,
          unitSymbol
        )
      }
    })
    .filter((datum) => datum.value)

  return {
    id,
    name,
    onLegendItemClick,
    data,
    activeData:
      activeLabels == null
        ? undefined
        : data.filter((datum) => activeLabels.has(datum.label))
  }
}

export const formatComparisonSeriesName = (seriesName: string) =>
  seriesName === 'Baseline' ? seriesName : 'WIS'

export const formatPipeLengthValue = (value?: number, unitSymbol?: string) => {
  if (value == null) {
    return undefined
  }

  const formattedValue = formatNumberWithCommas(Number(value), {
    maximumFractionDigits: 0
  })

  return `${formattedValue === '-0' ? '0' : formattedValue}${
    unitSymbol ?? ''
  } ft`
}

export const formatRiskValue = (value?: number, unitSymbol?: string) => {
  if (value == null) {
    return undefined
  }

  return `${chopAndFormatNumberWithCommas(Number(value))}${unitSymbol ?? ''}`
}

const formatPieTooltipValue = (
  value: number,
  pieTotal: number,
  isRiskSignificance: boolean,
  unitSymbol?: string
) => {
  const formattedValue = isRiskSignificance
    ? formatPipeLengthValue(value, unitSymbol)
    : formatRiskValue(value, unitSymbol)

  if (formattedValue == null) {
    return undefined
  }

  return `${formattedValue} (${formatPiePercentage(value, pieTotal)})`
}

const formatPiePercentage = (value: number, pieTotal: number) => {
  if (pieTotal === 0) {
    return '0%'
  }

  return `${formatNumberWithCommas((value / pieTotal) * 100, {
    maximumFractionDigits: 1
  })}%`
}

// apply opacity blend without adding alpha channel
export const wisRiskBySystemColorRange = TIMP_CHART_COLORS.map((color) =>
  chroma.mix(color, '#ffffff', 0.5, 'rgb').hex()
)
